/**
 * Media utilities for image and video handling
 */

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
const VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB in bytes

/**
 * Check if file is a video based on extension or MIME type
 */
export const isVideoFile = (fileName: string, mimeType?: string): boolean => {
  // Check MIME type first (more reliable)
  if (mimeType && VIDEO_MIME_TYPES.includes(mimeType)) {
    return true;
  }

  // Fallback to extension check
  const lowerName = fileName.toLowerCase();
  return VIDEO_EXTENSIONS.some(ext => lowerName.endsWith(ext));
};

/**
 * Check if URL is a video based on extensions
 * NOTE: This is now deprecated - use the 'type' field from backend instead
 * @deprecated Use PostImage.type === 'video' instead
 */
export const isVideoUrl = (url: string): boolean => {
  // Check for legacy #video marker for backward compatibility
  if (url.includes('#video')) {
    return true;
  }

  // Fallback: check for video extensions in URL
  const lowerUrl = url.toLowerCase();
  return VIDEO_EXTENSIONS.some(ext => lowerUrl.includes(ext));
};

/**
 * Validate video file
 */
export const validateVideoFile = (file: File): { valid: boolean; error?: string } => {
  // Check if it's actually a video
  if (!isVideoFile(file.name, file.type)) {
    return {
      valid: false,
      error: 'File không phải định dạng video hợp lệ. Chỉ hỗ trợ MP4, WebM.'
    };
  }

  // Check file size (100MB max)
  if (file.size > MAX_VIDEO_SIZE) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    return {
      valid: false,
      error: `Video quá lớn (${sizeMB}MB). Giới hạn tối đa 100MB.`
    };
  }

  // Only allow MP4 and WebM for browser compatibility
  if (!file.type.includes('mp4') && !file.type.includes('webm')) {
    return {
      valid: false,
      error: 'Chỉ hỗ trợ video MP4 và WebM để đảm bảo tương thích trình duyệt.'
    };
  }

  return { valid: true };
};

/**
 * Format file size to human readable
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

/**
 * Generate thumbnail from video file
 */
export const generateVideoThumbnail = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Cannot get canvas context'));
      return;
    }

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      // Seek to 1 second or 10% of duration
      video.currentTime = Math.min(1, video.duration * 0.1);
    };

    video.onseeked = () => {
      // Set canvas size to video size
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          resolve(url);
        } else {
          reject(new Error('Failed to generate thumbnail'));
        }

        // Cleanup
        URL.revokeObjectURL(video.src);
      }, 'image/jpeg', 0.8);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video'));
      URL.revokeObjectURL(video.src);
    };

    // Load video
    video.src = URL.createObjectURL(file);
  });
};

/**
 * Get video duration
 */
export const getVideoDuration = async (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      resolve(video.duration);
      URL.revokeObjectURL(video.src);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video'));
      URL.revokeObjectURL(video.src);
    };

    video.src = URL.createObjectURL(file);
  });
};

/**
 * Format duration in seconds to MM:SS
 */
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Convert Google Drive URL to embeddable video URL
 * Supports multiple URL formats and returns proper embed URL
 */
export const getGoogleDriveVideoUrl = (url: string): string => {
  // Remove #video marker if present
  const cleanUrl = url.replace('#video', '');

  // Extract file ID from various Google Drive URL formats
  let fileId = '';

  // Format 1: https://lh3.googleusercontent.com/d/{FILE_ID}
  const match1 = cleanUrl.match(/googleusercontent\.com\/d\/([^\/\?#]+)/);
  if (match1) {
    fileId = match1[1];
  }

  // Format 2: https://drive.google.com/file/d/{FILE_ID}/view or /preview
  const match2 = cleanUrl.match(/drive\.google\.com\/file\/d\/([^\/\?#]+)/);
  if (match2) {
    fileId = match2[1];
  }

  // Format 3: https://drive.google.com/uc?id={FILE_ID}
  const match3 = cleanUrl.match(/[?&]id=([^&]+)/);
  if (match3) {
    fileId = match3[1];
  }

  if (fileId) {
    // Return Google Drive preview URL for embedding
    // This format is specifically designed for iframe embedding
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }

  // If no file ID found, return original URL
  console.warn('Could not extract Google Drive file ID from URL:', url);
  return cleanUrl;
};
